import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useCallback } from "react";
import type { ContractOverview, NftPickerProps, NftPickerSelection, NftPickerSelectionError, ParseError, TokenRange, TokenSelection, OutputMode } from "../types";
import { toCanonicalRanges, mergeCanonicalRanges, removeTokenFromRanges, fromCanonicalRanges, tryToNumberArray, isRangeTooLargeError, formatCanonical, MAX_SAFE } from "../utils";
const EMPTY_SELECTION: TokenSelection = [];
type UseNftSelectionProps = {
  value?: NftPickerProps["value"] | undefined;
  defaultValue?: NftPickerProps["defaultValue"] | undefined;
  onChange: NftPickerProps["onChange"];
  onContractChange?: NftPickerProps["onContractChange"] | undefined;
  fixedContract?: NftPickerProps["fixedContract"] | undefined;
  outputMode: OutputMode;
};
export function useNftSelection(t0) {
  const $ = _c(53);
  const {
    value,
    defaultValue,
    onChange,
    onContractChange,
    fixedContract,
    outputMode
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [ranges, setRanges] = useState(t1);
  const [selectedContract, setSelectedContract] = useState(null);
  const effectiveSelectedContract = fixedContract ?? selectedContract;
  const [allSelected, setAllSelected] = useState(false);
  const previousRangesRef = useRef(null);
  const hasHydratedInitialValueRef = useRef(false);
  const onContractChangeRef = useRef(onContractChange);
  const isControlled = value !== undefined;
  let t2;
  let t3;
  if ($[1] !== onContractChange) {
    t2 = () => {
      onContractChangeRef.current = onContractChange;
    };
    t3 = [onContractChange];
    $[1] = onContractChange;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = contract => {
      onContractChangeRef.current?.(contract);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const emitContractChange = t4;
  const getSelectionFromRanges = _temp;
  let t5;
  if ($[5] !== onChange || $[6] !== outputMode) {
    t5 = (contract_0, canonicalRanges_0, isAll_0) => {
      const {
        selection: selectionIds,
        error: error_0
      } = getSelectionFromRanges(canonicalRanges_0, isAll_0);
      if (error_0) {
        return {
          error: error_0
        };
      }
      if (selectionIds === null) {
        return {};
      }
      if (!selectionIds.length && !isAll_0) {
        const payload = {
          contractAddress: contract_0.address,
          allSelected: isAll_0,
          outputMode,
          tokenIds: []
        } as NftPickerSelection;
        onChange(payload);
        return {};
      }
      if (outputMode === "number") {
        const {
          numbers,
          unsafeCount: unsafe
        } = tryToNumberArray(selectionIds);
        if (unsafe > 0) {
          const errorPayload = {
            type: "error",
            error: "Token IDs exceed Number.MAX_SAFE_INTEGER. Switch to bigint output or remove those IDs.",
            unsafeCount: unsafe,
            contractAddress: contract_0.address,
            outputMode: "number"
          };
          console.warn("NftPicker: selection includes token IDs above Number.MAX_SAFE_INTEGER; emitting error payload", {
            unsafeCount: unsafe,
            contractAddress: contract_0.address
          });
          onChange(errorPayload);
          return {
            unsafeCount: unsafe
          };
        }
        const payload_0 = {
          contractAddress: contract_0.address,
          allSelected: isAll_0,
          outputMode: "number",
          tokenIds: numbers
        };
        onChange(payload_0);
      } else {
        const decimalIds = selectionIds.map(_temp2);
        const payload_1 = {
          contractAddress: contract_0.address,
          allSelected: isAll_0,
          outputMode: "bigint",
          tokenIds: decimalIds
        };
        if (selectionIds.some(_temp3)) {
          console.warn("NftPicker: emitting bigint payload because some token IDs exceed MAX_SAFE_INTEGER");
        }
        onChange(payload_1);
      }
      return {};
    };
    $[5] = onChange;
    $[6] = outputMode;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const emitChange = t5;
  let t6;
  let t7;
  if ($[8] !== value) {
    t6 = () => {
      if (!value) {
        return;
      }
      hasHydratedInitialValueRef.current = true;
      if (value.contractAddress) {
        setSelectedContract(prev => prev?.address === value.contractAddress ? prev : null);
      } else {
        setSelectedContract(null);
      }
      const canonical_0 = toCanonicalRanges(value.selectedIds ?? EMPTY_SELECTION);
      setRanges(canonical_0);
      if (value.allSelected) {
        setAllSelected(true);
        previousRangesRef.current = canonical_0;
      } else {
        setAllSelected(false);
        previousRangesRef.current = null;
      }
    };
    t7 = [value];
    $[8] = value;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] !== defaultValue || $[12] !== isControlled) {
    t8 = () => {
      if (isControlled) {
        return;
      }
      if (hasHydratedInitialValueRef.current) {
        return;
      }
      if (!defaultValue) {
        return;
      }
      const defaultSelection = defaultValue.selectedIds ? Array.from(defaultValue.selectedIds) as TokenSelection : EMPTY_SELECTION;
      const canonical_1 = toCanonicalRanges(defaultSelection);
      setRanges(canonical_1);
      if (defaultValue.allSelected) {
        setAllSelected(true);
        previousRangesRef.current = canonical_1;
      } else {
        setAllSelected(false);
        previousRangesRef.current = null;
      }
      hasHydratedInitialValueRef.current = true;
    };
    t9 = [isControlled, defaultValue];
    $[11] = defaultValue;
    $[12] = isControlled;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  if ($[15] !== effectiveSelectedContract || $[16] !== emitChange || $[17] !== ranges) {
    t10 = newRanges => {
      if (!newRanges.length) {
        return;
      }
      const canonical_2 = mergeCanonicalRanges(ranges, newRanges);
      const result = effectiveSelectedContract ? emitChange(effectiveSelectedContract, canonical_2, false) : {};
      if ("error" in result) {
        return result;
      }
      setRanges(canonical_2);
      setAllSelected(false);
      previousRangesRef.current = null;
      return result;
    };
    $[15] = effectiveSelectedContract;
    $[16] = emitChange;
    $[17] = ranges;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const addTokenRanges = t10;
  let t11;
  if ($[19] !== effectiveSelectedContract || $[20] !== emitChange || $[21] !== ranges) {
    t11 = tokenId => {
      const canonical_3 = removeTokenFromRanges(ranges, tokenId);
      setRanges(canonical_3);
      setAllSelected(false);
      previousRangesRef.current = null;
      if (effectiveSelectedContract) {
        emitChange(effectiveSelectedContract, canonical_3, false);
      }
    };
    $[19] = effectiveSelectedContract;
    $[20] = emitChange;
    $[21] = ranges;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const removeToken = t11;
  let t12;
  if ($[23] !== effectiveSelectedContract || $[24] !== emitChange) {
    t12 = () => {
      if (!effectiveSelectedContract) {
        return;
      }
      setRanges([]);
      setAllSelected(false);
      previousRangesRef.current = null;
      emitChange(effectiveSelectedContract, [], false);
    };
    $[23] = effectiveSelectedContract;
    $[24] = emitChange;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const clearTokens = t12;
  let t13;
  if ($[26] !== allSelected || $[27] !== effectiveSelectedContract || $[28] !== emitChange || $[29] !== ranges) {
    t13 = () => {
      if (!effectiveSelectedContract || allSelected) {
        return;
      }
      previousRangesRef.current = ranges;
      setAllSelected(true);
      emitChange(effectiveSelectedContract, ranges, true);
    };
    $[26] = allSelected;
    $[27] = effectiveSelectedContract;
    $[28] = emitChange;
    $[29] = ranges;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const selectAll = t13;
  let t14;
  if ($[31] !== effectiveSelectedContract || $[32] !== emitChange || $[33] !== ranges) {
    t14 = () => {
      if (!effectiveSelectedContract) {
        return;
      }
      const previous = previousRangesRef.current;
      setAllSelected(false);
      if (previous?.length) {
        setRanges(previous);
        previousRangesRef.current = null;
        emitChange(effectiveSelectedContract, previous, false);
        return;
      }
      emitChange(effectiveSelectedContract, ranges, false);
    };
    $[31] = effectiveSelectedContract;
    $[32] = emitChange;
    $[33] = ranges;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const deselectAll = t14;
  let t15;
  if ($[35] !== fixedContract || $[36] !== onChange) {
    t15 = () => {
      if (fixedContract) {
        return;
      }
      setSelectedContract(null);
      setRanges([]);
      setAllSelected(false);
      emitContractChange(null);
      onChange(null);
      previousRangesRef.current = null;
    };
    $[35] = fixedContract;
    $[36] = onChange;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const clearContract = t15;
  let t16;
  if ($[38] !== effectiveSelectedContract || $[39] !== emitChange) {
    t16 = canonical_4 => {
      const result_0 = effectiveSelectedContract ? emitChange(effectiveSelectedContract, canonical_4, false) : {};
      if ("error" in result_0) {
        return result_0;
      }
      setRanges(canonical_4);
      setAllSelected(false);
      previousRangesRef.current = null;
      return result_0;
    };
    $[38] = effectiveSelectedContract;
    $[39] = emitChange;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const setSelectionFromText = t16;
  let t17;
  if ($[41] !== addTokenRanges || $[42] !== allSelected || $[43] !== clearContract || $[44] !== clearTokens || $[45] !== deselectAll || $[46] !== effectiveSelectedContract || $[47] !== emitChange || $[48] !== ranges || $[49] !== removeToken || $[50] !== selectAll || $[51] !== setSelectionFromText) {
    t17 = {
      ranges,
      setRanges,
      selectedContract: effectiveSelectedContract,
      setSelectedContract,
      allSelected,
      setAllSelected,
      emitContractChange,
      emitChange,
      addTokenRanges,
      removeToken,
      clearTokens,
      selectAll,
      deselectAll,
      clearContract,
      setSelectionFromText
    };
    $[41] = addTokenRanges;
    $[42] = allSelected;
    $[43] = clearContract;
    $[44] = clearTokens;
    $[45] = deselectAll;
    $[46] = effectiveSelectedContract;
    $[47] = emitChange;
    $[48] = ranges;
    $[49] = removeToken;
    $[50] = selectAll;
    $[51] = setSelectionFromText;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  return t17;
}
function _temp3(id_0) {
  return id_0 > MAX_SAFE;
}
function _temp2(id) {
  return id.toString(10);
}
function _temp(canonicalRanges, isAll) {
  if (isAll) {
    return {
      selection: []
    };
  }
  ;
  try {
    return {
      selection: fromCanonicalRanges(canonicalRanges)
    };
  } catch (t0) {
    const error = t0;
    if (!isRangeTooLargeError(error)) {
      throw error;
    }
    const canonical = formatCanonical(canonicalRanges);
    const displayValue = canonical || "selection";
    return {
      selection: null,
      error: {
        code: error.code,
        input: displayValue,
        index: 0,
        length: Math.max(displayValue.length, 1),
        message: error.message
      }
    };
  }
}